import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { CheckCircle, Loader2, AlertCircle, Sparkles, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { API } from '../config/api';

export default function SubscriptionSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const [paymentData, setPaymentData] = useState(null);
  const [pollAttempts, setPollAttempts] = useState(0);
  
  const sessionId = searchParams.get('session_id');

  const pollPaymentStatus = useCallback(async (attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;
    
    if (attempts >= maxAttempts) {
      setStatus('error');
      setErrorMessage('Payment verification timed out. Please contact support.');
      return;
    }
    
    try {
      const response = await axios.get(`${API}/stripe/checkout/status/${sessionId}`);
      const data = response.data;
      
      setPaymentData(data);
      
      if (data.payment_status === 'paid') {
        // Payment confirmed! Complete registration
        const pendingData = localStorage.getItem('pendingRegistration');
        
        if (pendingData) {
          const registrationData = JSON.parse(pendingData);
          
          // Check if data is not too old (1 hour max)
          const oneHour = 60 * 60 * 1000;
          if (Date.now() - registrationData.timestamp > oneHour) {
            localStorage.removeItem('pendingRegistration');
            setStatus('error');
            setErrorMessage('Registration session expired. Please try again.');
            return;
          }
          
          try {
            // Complete the registration
            await register(
              registrationData.email,
              registrationData.password,
              registrationData.fullName,
              registrationData.role
            );
            
            // Clear pending registration
            localStorage.removeItem('pendingRegistration');
            
            setStatus('success');
            toast.success('Account created successfully!');
            
            // Redirect to onboarding for athletes and parents
            setTimeout(() => {
              if (registrationData.role === 'athlete' || registrationData.role === 'parent') {
                navigate('/onboarding');
              } else {
                navigate('/dashboard');
              }
            }, 3000);
          } catch (regError) {
            console.error('Registration error:', regError);
            // Payment successful but registration failed
            setStatus('success');
            toast.info('Payment successful! Please log in with your credentials.');
            localStorage.removeItem('pendingRegistration');
            setTimeout(() => navigate('/login'), 3000);
          }
        } else {
          // No pending registration - user might have already completed
          setStatus('success');
          toast.success('Welcome! Your subscription is active.');
          setTimeout(() => navigate('/login'), 3000);
        }
        return;
      } else if (data.status === 'expired') {
        setStatus('error');
        setErrorMessage('Payment session expired. Please try again.');
        return;
      }
      
      // Continue polling
      setPollAttempts(attempts + 1);
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      
    } catch (error) {
      console.error('Payment status check error:', error);
      setPollAttempts(attempts + 1);
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    }
  }, [sessionId, register, navigate]);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(0);
    } else {
      // No session ID - try to complete registration anyway (might be from old link)
      const pendingData = localStorage.getItem('pendingRegistration');
      if (pendingData) {
        completeRegistrationWithoutPayment();
      } else {
        setStatus('success');
        toast.success('Welcome! Your subscription is active.');
        setTimeout(() => navigate('/login'), 3000);
      }
    }
  }, [sessionId, pollPaymentStatus]);

  const completeRegistrationWithoutPayment = async () => {
    try {
      const pendingData = localStorage.getItem('pendingRegistration');
      
      if (!pendingData) {
        setStatus('success');
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      const registrationData = JSON.parse(pendingData);
      
      const oneHour = 60 * 60 * 1000;
      if (Date.now() - registrationData.timestamp > oneHour) {
        localStorage.removeItem('pendingRegistration');
        setStatus('error');
        setErrorMessage('Registration session expired. Please try again.');
        return;
      }

      await register(
        registrationData.email,
        registrationData.password,
        registrationData.fullName,
        registrationData.role
      );

      localStorage.removeItem('pendingRegistration');
      
      setStatus('success');
      toast.success('Account created successfully!');
      
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (error) {
      console.error('Registration error:', error);
      setStatus('error');
      setErrorMessage(error.response?.data?.detail || 'Failed to complete registration');
    }
  };
  
  const formatCurrency = (amount, currency) => {
    const formatter = new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency?.toUpperCase() || 'GBP'
    });
    return formatter.format(amount / 100);
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4 py-12" data-testid="subscription-success-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_youth-athlete-lab/artifacts/pr7l13yi_Elite%20%284%29.png" 
            alt="BE ELITE APP" 
            className="h-32 mx-auto mb-4"
          />
        </div>

        <div className="bg-zinc-900 rounded-2xl shadow-xl p-8 border border-zinc-800 text-center">
          {status === 'processing' && (
            <>
              <Loader2 className="h-16 w-16 text-[#02b5ff] animate-spin mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-white mb-3">
                {sessionId ? 'Verifying Payment' : 'Setting Up Your Account'}
              </h2>
              <p className="text-gray-400 mb-4">
                {sessionId ? 'Confirming your payment with Stripe...' : 'Please wait while we complete your registration...'}
              </p>
              {/* Poll progress indicator */}
              {sessionId && (
                <div className="flex justify-center gap-1 mt-4">
                  {[...Array(10)].map((_, i) => (
                    <div 
                      key={i}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        i <= pollAttempts ? 'bg-[#02b5ff]' : 'bg-zinc-700'
                      }`}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {status === 'success' && (
            <>
              <div className="bg-green-500/20 rounded-full p-4 w-fit mx-auto mb-6">
                <CheckCircle className="h-16 w-16 text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-3">Welcome to BE ELITE APP!</h2>
              <p className="text-gray-400 mb-4">
                Your subscription is now active with a 30-day free trial.
              </p>
              
              {/* Payment Details */}
              {paymentData && (
                <div className="bg-zinc-800/50 rounded-lg p-4 mb-4 text-left">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-zinc-400 text-sm">Package</span>
                    <span className="text-white font-medium">
                      {paymentData.metadata?.package_name || 'Monthly Subscription'}
                    </span>
                  </div>
                  {paymentData.amount_total > 0 && (
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-zinc-400 text-sm">Amount</span>
                      <span className="text-white font-medium">
                        {formatCurrency(paymentData.amount_total, paymentData.currency)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between items-center">
                    <span className="text-zinc-400 text-sm">Status</span>
                    <span className="text-green-400 font-medium flex items-center gap-1">
                      <Sparkles className="h-4 w-4" />
                      Active
                    </span>
                  </div>
                </div>
              )}
              
              <div className="bg-zinc-800/50 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-300">
                  <span className="text-green-400 font-semibold">✓ 30 days FREE</span> - No charge until trial ends
                </p>
                <p className="text-sm text-gray-300 mt-1">
                  Then only <span className="text-white font-semibold">£2.99/month</span>
                </p>
              </div>
              <p className="text-sm text-gray-500">
                Redirecting you to the dashboard...
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="bg-red-500/20 rounded-full p-4 w-fit mx-auto mb-6">
                <AlertCircle className="h-16 w-16 text-red-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-3">Something Went Wrong</h2>
              <p className="text-gray-400 mb-6">
                {errorMessage}
              </p>
              <div className="space-y-3">
                <Button
                  onClick={() => navigate('/register')}
                  className="w-full bg-gradient-to-r from-[#02b5ff] to-[#0099dd] hover:from-[#0099dd] hover:to-[#0088cc] text-white"
                  data-testid="retry-registration-btn"
                >
                  Try Again
                </Button>
                <Button
                  onClick={() => navigate('/login')}
                  variant="outline"
                  className="w-full border-zinc-700 text-white hover:bg-zinc-800"
                  data-testid="go-to-login-btn"
                >
                  Go to Login
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-4">
                Need help? Contact support@eliteplayerperformance.co.uk
              </p>
            </>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-gray-600 text-sm mt-6">
          © BE ELITE APP
        </p>
      </div>
    </div>
  );
}
